import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaClock,
  FaPaw,
  FaHeart,
  FaCheckCircle,
  FaDog,
  FaWalking,
  FaUtensils,
  FaBed,
  FaPlay,
  FaShieldAlt,
  FaCertificate,
  FaLeaf
} from 'react-icons/fa';
import dogImage from '../assets/chienmangerrose.webp';
import './AboutPage.css';
import SEO from './SEO';

const AboutPage: React.FC = () => {
  const navigate = useNavigate();
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const daySchedule = [
    {
      time: "En matinée",
      title: "Arrivée & Accueil",
      description: "Accueil personnalisé de chaque animal avec rituel de bienvenue et acclimatation progressive à l'environnement.",
      icon: FaPaw,
      color: "#f6c1c7"
    },
    {
      time: "Matin",
      title: "Activités Matinales",
      description: "Jeux stimulants, exercices physiques et activités cognitives selon la météo et les préférences de chaque animal.",
      icon: FaPlay,
      color: "#e8a5ad"
    },
    {
      time: "Mi-journée",
      title: "Repos & Repas",
      description: "Repas avec la nourriture apportée par les propriétaires, suivis d'une période de repos calme.",
      icon: FaUtensils,
      color: "#f6c1c7"
    },
    {
      time: "Après-midi",
      title: "Promenades",
      description: "Sorties dans les espaces verts environnants pour l'exercice physique et la stimulation mentale.",
      icon: FaWalking,
      color: "#e8a5ad"
    },
    {
      time: "Fin d'après-midi",
      title: "Jeux Libres",
      description: "Session de socialisation et jeux libres sous surveillance attentive pour renforcer les liens entre animaux.",
      icon: FaHeart,
      color: "#f6c1c7"
    },
    {
      time: "En soirée",
      title: "Retour au Calme",
      description: "Moment de détente, photos et vidéos envoyées aux propriétaires avant le retour.",
      icon: FaBed,
      color: "#e8a5ad"
    }
  ];

  const pricingPlans = [
    {
      name: "Garde Journalière",
      price: "15€",
      period: "par jour",
      features: [
        "Accueil de 7h à 19h",
        "Activités et promenades incluses",
        "Nourriture apportée par le propriétaire",
        "Photos et vidéos quotidiennes",
        "Rapport de comportement"
      ],
      popular: true,
      icon: FaDog
    },
    {
      name: "Demi-Journée",
      price: "12€",
      period: "jusqu'à 5h",
      features: [
        "Durée maximale : 5h",
        "Activités adaptées",
        "Nourriture apportée par le propriétaire",
        "Flexibilité horaire",
        "Idéal pour découvrir"
      ],
      popular: false,
      icon: FaClock
    }
  ];

  const additionalServices = [
    {
      name: "Promenade des chiens",
      price: "13€",
      description: "Sortie encadrée et sécurisée dans les espaces verts environnants",
      icon: FaWalking
    },
    {
      name: "Garde à domicile NAC",
      price: "10€ / jour",
      description: "Garde à domicile dédiée aux NAC (lapins, rongeurs, oiseaux...) : confort de leur environnement habituel",
      icon: FaLeaf
    },
    {
      name: "Visite à domicile (chat ou NAC)",
      price: "12€",
      description: "Visite à domicile pour les chats ou NAC : alimentation, jeux, câlins",
      icon: FaPaw
    },
    {
      name: "Adaptation",
      price: "15€",
      description: "Séance d'adaptation obligatoire avant toute garde définitive (environ 1h) : découverte progressive de l'environnement, évaluation du comportement et rencontre avec les autres animaux. La garantie d'un bon démarrage.",
      icon: FaLeaf
    },
    {
      name: "Toilettage",
      price: "30€",
      description: "Bain, coupe, brossage et soins selon la race et les besoins",
      icon: FaShieldAlt
    },
    {
      name: "Forfaits longue durée",
      price: "Sur devis",
      description: "Forfaits semaine, week-end et vacances. Demandez votre devis personnalisé via le formulaire de contact.",
      icon: FaCertificate
    }
  ];

  return (
    <div className="about-page">
      <SEO
        title="À Propos — Émilie, votre dog sitter à Arpajon"
        description="Découvrez l'approche personnalisée de la garderie d'Émilie à Arpajon : journée type, tarifs transparents, services pour chiens et NAC, environnement sécurisé."
        path="/about"
      />
      <section className="about-hero">
        <div className="hero-background">
          <img src={dogImage} alt="Chien qui joue à la garderie d'Arpajon" className="hero-image" loading="eager" fetchPriority="high" decoding="async" width="1920" height="1080" />
          <div className="hero-overlay"></div>
        </div>
        <div className="hero-content">
          <motion.div 
            className="hero-text"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <h1 className="hero-title">À Propos de Notre Garderie à Arpajon (91)</h1>
            <p className="hero-subtitle">
              Découvrez notre approche personnalisée et nos tarifs transparents
            </p>
            <div className="hero-features">
              <div className="feature-item">
                <FaHeart className="feature-icon" />
                <span>Amour & Soins</span>
              </div>
              <div className="feature-item">
                <FaShieldAlt className="feature-icon" />
                <span>Professionnels</span>
              </div>
              <div className="feature-item">
                <FaLeaf className="feature-icon" />
                <span>Environnement Naturel</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section "Rencontrez Emilie" - bio detaillee */}
      <section className="emilie-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2>Rencontrez Émilie</h2>
            <p>Votre dog sitter passionnée à Arpajon</p>
          </motion.div>

          <motion.div
            className="emilie-grid"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="emilie-bio">
              <p className="emilie-intro">
                Je suis depuis toute petite avec des animaux : lapins, chiens, chats, chevaux,
                poissons, souris, furets et perruches. Je me suis occupée des animaux de
                ma famille — trois chiens, deux chevaux, deux souris, trois perruches et trois chats.
              </p>

              <p>
                Cela fait plusieurs années que je garde vos compagnons, soit à la garderie,
                soit à votre domicile, pour&nbsp;:
              </p>
              <ul className="emilie-list">
                <li>Donner à manger à vos boules de poils</li>
                <li>Les sortir et les promener</li>
                <li>Nettoyer leur espace</li>
                <li>Partager leurs moments préférés : câlins et jeux</li>
              </ul>

              <p>
                Je suis quelqu'un de <strong>généreuse et sérieuse</strong>. Je respecte les règles
                des familles : je n'irai jamais à l'encontre de leurs habitudes. Je peux administrer
                les médicaments par voie orale (pas par voie intraveineuse).
              </p>

              <p>
                Je propose également du <strong>toilettage</strong> pour les animaux en garde.
              </p>

              <p className="emilie-quote-personal">
                « J'adore les animaux. Sans eux, je suis triste — ils sont une bouffée
                de chaleur et d'amour, comme un enfant, avec leurs moments de bêtise et
                de gentillesse. »
              </p>

              <p className="emilie-cta-text">
                Si mon profil vous a plu, n'hésitez pas à me contacter — ce sera avec plaisir
                que je vous rencontrerai, vous et votre quatre-pattes.
              </p>
            </div>

            <aside className="emilie-card">
              <div className="emilie-card-block">
                <h3>Diplômes &amp; Formations</h3>
                <ul className="emilie-creds">
                  <li>
                    <FaCertificate aria-hidden="true" />
                    <span><strong>ACACED</strong> — Attestation de Connaissances pour les Animaux de Compagnie d'Espèces Domestiques</span>
                  </li>
                  <li>
                    <FaCertificate aria-hidden="true" />
                    <span><strong>ASV</strong> — Auxiliaire Spécialisée Vétérinaire</span>
                  </li>
                  <li>
                    <FaCertificate aria-hidden="true" />
                    <span><strong>Toilettage</strong> — Formation en cours</span>
                  </li>
                </ul>
              </div>

              <div className="emilie-card-block">
                <h3>Mes valeurs</h3>
                <ul className="emilie-values">
                  <li><FaHeart aria-hidden="true" /> 5 animaux maximum simultanément</li>
                  <li><FaShieldAlt aria-hidden="true" /> Respect strict des règles familiales</li>
                  <li><FaPaw aria-hidden="true" /> Photos et vidéos quotidiennes</li>
                </ul>
              </div>

              <div className="emilie-card-block emilie-pets-block">
                <h3>Animaux que j'accueille avec passion</h3>
                <div className="emilie-pets">
                  <span>🐶 Chiens</span>
                  <span>🐱 Chats</span>
                  <span>🐰 Lapins</span>
                  <span>🐹 Rongeurs</span>
                  <span>🐴 Chevaux</span>
                </div>
              </div>
            </aside>
          </motion.div>

          <motion.figure
            className="emilie-quotes"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <blockquote>
              « Les animaux ne manquent pas de raison, ils manquent seulement de paroles. »
              <cite>— Jean de La Fontaine</cite>
            </blockquote>
            <blockquote>
              « J'ai toujours préféré la compagnie des animaux à celle des hommes. Ils sont plus simples. »
              <cite>— Ernest Hemingway</cite>
            </blockquote>
          </motion.figure>
        </div>
      </section>

      <section className="approach-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2>Notre Approche</h2>
            <p>Une méthode unique basée sur le respect et l'individualité de chaque chien</p>
          </motion.div>

          <motion.div 
            className="approach-grid"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <motion.div className="approach-card" variants={fadeInUp}>
              <div className="approach-icon">
                <FaHeart />
              </div>
              <h3>Soins Personnalisés</h3>
              <p>Chaque animal reçoit une attention adaptée à ses besoins. Photos et vidéos quotidiennes envoyées aux propriétaires.</p>
            </motion.div>

            <motion.div className="approach-card" variants={fadeInUp}>
              <div className="approach-icon">
                <FaShieldAlt />
              </div>
              <h3>Environnement Sécurisé</h3>
              <p>La garderie est entièrement sécurisée avec des espaces adaptés et une surveillance constante.</p>
            </motion.div>

            <motion.div className="approach-card" variants={fadeInUp}>
              <div className="approach-icon">
                <FaCertificate />
              </div>
              <h3>Équipe Qualifiée</h3>
              <p>Émilie est formée et certifiée pour assurer le bien-être et la sécurité de vos compagnons.</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="schedule-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2>Une Journée Type</h2>
            <p>Organisation indicative, adaptée aux besoins de chaque animal</p>
          </motion.div>

          <motion.div 
            className="schedule-timeline"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {daySchedule.map((activity, index) => {
              const IconComponent = activity.icon;
              return (
                <motion.div 
                  key={index}
                  className="schedule-item"
                  variants={fadeInUp}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="schedule-time">
                    <span className="time">{activity.time}</span>
                  </div>
                  <div className="schedule-content">
                    <div className="schedule-icon" style={{ backgroundColor: activity.color }}>
                      <IconComponent />
                    </div>
                    <div className="schedule-text">
                      <h3>{activity.title}</h3>
                      <p>{activity.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="pricing-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2>Nos Tarifs</h2>
            <p>Des prix transparents et des formules adaptées à tous les besoins</p>
          </motion.div>

          <motion.div 
            className="pricing-grid"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {pricingPlans.map((plan, index) => {
              const IconComponent = plan.icon;
              return (
                <motion.div 
                  key={index}
                  className={`pricing-card ${plan.popular ? 'popular' : ''}`}
                  variants={fadeInUp}
                  whileHover={{ y: -8 }}
                >
                  {plan.popular && (
                    <div className="popular-badge">Plus Populaire</div>
                  )}
                  <div className="pricing-header">
                    <div className="pricing-icon">
                      <IconComponent />
                    </div>
                    <h3>{plan.name}</h3>
                    <div className="pricing-price">
                      <span className="price">{plan.price}</span>
                      <span className="period">{plan.period}</span>
                    </div>
                  </div>
                  <ul className="pricing-features">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex}>
                        <FaCheckCircle className="check-icon" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact" className="pricing-button">
                    Réserver maintenant
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="approach-section" style={{ background: '#fdf0f2' }}>
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2>Informations Importantes</h2>
            <p>À lire avant toute réservation</p>
          </motion.div>
          <motion.div
            className="approach-grid"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <motion.div className="approach-card" variants={fadeInUp}>
              <div className="approach-icon">
                <FaUtensils />
              </div>
              <h3>Nourriture non incluse</h3>
              <p>Les croquettes et repas ne sont pas fournis. Le propriétaire doit apporter la nourriture habituelle de son animal.</p>
            </motion.div>
            <motion.div className="approach-card" variants={fadeInUp}>
              <div className="approach-icon">
                <FaShieldAlt />
              </div>
              <h3>Documents requis</h3>
              <p>Carnet de vaccination à jour et passeport de l'animal obligatoires avant toute prise en charge.</p>
            </motion.div>
            <motion.div className="approach-card" variants={fadeInUp}>
              <div className="approach-icon">
                <FaHeart />
              </div>
              <h3>Adaptation obligatoire</h3>
              <p>Une séance d'adaptation est requise avant toute garde définitive, pour assurer le confort et la sécurité de votre animal.</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="additional-services-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2>Services Supplémentaires</h2>
            <p>Des options pour personnaliser encore plus l'expérience de votre chien</p>
          </motion.div>

          <motion.div 
            className="services-grid"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {additionalServices.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <motion.div 
                  key={index}
                  className="service-card"
                  variants={fadeInUp}
                  whileHover={{ y: -4 }}
                >
                  <div className="service-icon">
                    <IconComponent />
                  </div>
                  <h3>{service.name}</h3>
                  <div className="service-price">{service.price}</div>
                  <p>{service.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="contact-section">
        <div className="container">
          <motion.div 
            className="contact-content"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2>Des Questions ?</h2>
            <p>Émilie est là pour vous accompagner et répondre à toutes vos questions</p>
            <div className="contact-actions">
              <button className="primary-button" onClick={() => navigate('/contact')}>
                Nous contacter
              </button>
              <button className="secondary-button" onClick={() => navigate('/calendar')}>
                Voir le planning
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
